/** @format */

import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { dateFun } from "../../../common-utilities/utils";

// ─── Row definitions ─────────────────────────────────────────────────────────
// yr1Key / yr2Key / yr3Key → exact column names from the FLAT Excel sheet.
// divisor → applied to numeric values before display (e.g. raw Rs → Mn).

const SITE_DETAILS_ROWS = [
  { label: "Total Carpet Area (Sq ft)", yr1Key: "Carpet Area (Sq ft)", yr2Key: null, yr3Key: null },
  { label: "No. of Seaters", yr1Key: "No. of Seaters", yr2Key: null, yr3Key: null },
];

const KEY_METRICS_ROWS = [
  { label: "Breakeven ADS", yr1Key: "Breakeven ADS", yr2Key: null, yr3Key: null, type: "currency" },
  { label: "ADS", bold: true, yr1Key: "Yr1 ADS in Rs", yr2Key: "ADS - Year 2", yr3Key: "ADS - Year 3", type: "currency" },
  { label: "ADT", yr1Key: "Yr 1 ADT", yr2Key: "ADT - Year 2", yr3Key: "ADT - Year 3" },
  { label: "Dine in ADT", indent: true, yr1Key: "Dine In ADT - Year 1", yr2Key: "Dine In ADT - Year 2", yr3Key: "Dine In ADT - Year 3" },
  { label: "Delivery ADT", indent: true, yr1Key: "Delivery ADT - Year 1", yr2Key: "Delivery ADT - Year 2", yr3Key: "Delivery ADT - Year 3" },
  { label: "APC", yr1Key: "APC - Year 1", yr2Key: "APC - Year 2", yr3Key: "APC - Year 3" },
  { label: "Delivery %", yr1Key: "Del% - Year 1", yr2Key: "Del% - Year 2", yr3Key: "Del% - Year 3", type: "percentage" },
  { label: "Monthly Occupancy", yr1Key: "Monthly Occupancy - Year 1", yr2Key: "Monthly Occupancy - Year 2", yr3Key: "Monthly Occupancy - Year 3", type: "currency" },
  { label: "Occupancy Cost %", yr1Key: "Occupancy % - year 1", yr2Key: "Occupancy % - year 2", yr3Key: "Occupancy % - year 3", type: "percentage", toFixed: 1 },
  { label: "Store EBITDA", bold: true, yr1Key: "Store EBITDA% - Year 1", yr2Key: "Store EBITDA% - Year 2", yr3Key: "Store EBITDA% - Year 3", type: "percentage", toFixed: 1},
  { label: "Monthly EBITDA", yr1Key: "Monthly Store EBITDA - Year 1", yr2Key: "Monthly Store EBITDA - Year 2", yr3Key: "Monthly Store EBITDA - Year 3", type: "currency" },
];

const INVESTMENT_ROWS = [
  { label: "Hardcost, net of LL investment (in Mn)", yr1Key: "Hardcost", yr2Key: null, yr3Key: null, divisor: 1000000, toFixed: 2 },
  { label: "Soft Cost (in Mn)", yr1Key: "Softcost", yr2Key: null, yr3Key: null, divisor: 1000000, toFixed: 2 },
  { label: "Security Deposit (in Mn)", yr1Key: "R. Sec Deposit", yr2Key: null, yr3Key: null, divisor: 1000000, toFixed: 2 },
  { label: "Total Investment (in Mn)", bold: true, yr1Key: "Total Invt Incl SD", yr2Key: null, yr3Key: null, divisor: 1000000, toFixed: 2 },
  { label: "Investment from LL (in Mn)", yr1Key: "LL Investment", yr2Key: null, yr3Key: null, divisor: 1000000, toFixed: 2 },
];

const RETURNS_ROWS = [
  { label: "Payback w/o marketing contribution, w SD (Years)", yr1Key: "Payback with SD (Years)", yr2Key: null, yr3Key: null, toFixed: 1 },
  { label: "Payback w/o marketing contribution, w SD (Months)", yr1Key: "Payback with SD (in months)", yr2Key: null, yr3Key: null },
  { label: "ROCE (Pre tax)", yr1Key: "ROCE (pre-tax)", yr2Key: null, yr3Key: null, type: "percentage" },
  { label: "IRR (5 years)", yr1Key: "IRR (5 years)", yr2Key: null, yr3Key: null, type: "percentage" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getCellValue = (data, key, divisor = 1, type = "number", toFixed = 0) => {
  if (!key || !data) return "—";
  const val = data[key];
  if (val == null) return "—";
  if (type === "currency") {
    return `${Math.round(val / divisor).toLocaleString()}`;
  }
  if (type === "percentage") {
    return (val * 100).toFixed(toFixed) + "%";
  }  
  if (!isNaN(val)) return (val / divisor).toFixed(toFixed);
  return String(val);
};

// ─── Shared cell sx ──────────────────────────────────────────────────────────

const CELL_BORDER = "1px solid #d0d0d0";

const headerCellSx = {
  border: CELL_BORDER,
  backgroundColor: "#008938",
  color: "#fff",
  fontWeight: 700,
  fontSize: "0.8rem",
  textAlign: "center",
  py: 1,
  px: 1.5,
};

const sectionDividerSx = {
  border: CELL_BORDER,
  fontWeight: 700,
  textDecoration: "underline",
  textTransform: "uppercase",
  fontSize: "0.75rem",
  backgroundColor: "#f5f5f5",
  py: 0.75,
  px: 1.5,
};

const subHeaderCellSx = {
  border: CELL_BORDER,
  fontWeight: 700,
  fontSize: "0.78rem",
  textAlign: "center",
  backgroundColor: "#fafafa",
  py: 0.75,
  px: 1.5,
};

const valueCellSx = (bold) => ({
  border: CELL_BORDER,
  textAlign: "center",
  fontWeight: bold ? 700 : 400,
  fontSize: "0.8rem",
  py: 0.5,
  px: 1.5,
});

const labelCellSx = (bold, indent) => ({
  border: CELL_BORDER,
  fontWeight: bold ? 700 : 400,
  fontSize: "0.8rem",
  pl: indent ? 4 : 1.5,
  py: 0.5,
});

// ─── Row renderers ────────────────────────────────────────────────────────────

const DataRow = ({ row, data }) => (
  <TableRow>
    <TableCell sx={labelCellSx(row.bold, row.indent)}>{row.label}</TableCell>
    <TableCell sx={valueCellSx(row.bold)}>{getCellValue(data, row.yr1Key, row.divisor, row.type, row.toFixed)}</TableCell>
    <TableCell sx={valueCellSx(row.bold)}>{getCellValue(data, row.yr2Key, row.divisor, row.type, row.toFixed)}</TableCell>
    <TableCell sx={valueCellSx(row.bold)}>{getCellValue(data, row.yr3Key, row.divisor, row.type, row.toFixed)}</TableCell>
  </TableRow>
);

const SectionDivider = ({ label }) => (
  <TableRow>
    <TableCell colSpan={4} sx={sectionDividerSx}>
      {label}
    </TableCell>
  </TableRow>
);

const SubHeader = ({ label }) => (
  <TableRow>
    <TableCell sx={{ ...subHeaderCellSx, textAlign: "left" }}>{label}</TableCell>
    <TableCell sx={subHeaderCellSx}>Year 1</TableCell>
    <TableCell sx={subHeaderCellSx}>Year 2</TableCell>
    <TableCell sx={subHeaderCellSx}>Year 3</TableCell>
  </TableRow>
);

// ─── Main component ───────────────────────────────────────────────────────────

const CfV2FinancialTable = ({ formId, financial }) => {

  const data = financial?.data || null;

  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Typography
        variant="subtitle2"
        sx={{ color: "#008938", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, mb: 1.5 }}
      >
        Financial Data
        {financial && (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ ml: 1, textTransform: "none", fontWeight: 400 }}
          >
            (Version {financial.version} · Last updated {dateFun(financial.updatedAt, true)})
          </Typography>
        )}
      </Typography>

      {!financial ? (
        <Typography variant="body2" color="text.secondary">
          No financial data uploaded yet.
        </Typography>
      ) : (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ borderCollapse: "collapse", minWidth: 560 }}>

            {/* ── Main header ── */}
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellSx, width: "45%", textAlign: "left" }}>COCO Build Site Details</TableCell>
                <TableCell sx={headerCellSx}>Year 1</TableCell>
                <TableCell sx={headerCellSx}>Year 2</TableCell>
                <TableCell sx={headerCellSx}>Year 3</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {/* ── Site Details ── */}
              {SITE_DETAILS_ROWS.map((row) => (
                <DataRow key={row.label} row={row} data={data} />
              ))}

              {/* ── Key Metrics ── */}
              <SectionDivider label="Key Metrics" />
              <SubHeader label="Key Metrics" />
              {KEY_METRICS_ROWS.map((row) => (
                <DataRow key={row.label} row={row} data={data} />
              ))}

              {/* ── Investment ── */}
              <SectionDivider label="Investment" />
              {INVESTMENT_ROWS.map((row) => (
                <DataRow key={row.label} row={row} data={data} />
              ))}

              {/* ── Returns ── */}
              <SectionDivider label="Returns" />
              {RETURNS_ROWS.map((row) => (
                <DataRow key={row.label} row={row} data={data} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default CfV2FinancialTable;
